import React, { useEffect } from 'react'
import {
  AnimalDto,
  AnimalDtoSpecies,
  AnimalVaccinationDtoVaccines,
  CreateAnimalBodyDtoVaccinationStatus,
} from '../../../../interactors/gen/backendClient'
import { Box, Grid, Typography } from '@mui/material'
import {
  getReadableAnimalVaccineStatus,
  getColorForAnimalVaccineStatus,
  getReadableVaccines,
} from '../../../../domain/Animal/AnimalDisplay'
import { ColoredChipSelectField } from '../../../common/ColoredChipSelectField'
import { useFormExtended } from '../../../../utils/hooks/useFormExtended'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { omitEmptyString } from '../../../../utils/omitEmptyString'
import { ControlledMultipleAutoComplete } from '../../../common/ControlledMultipleAutoComplete'
import { isVaccineForCat, isVaccineForDog } from '../../../../domain/Animal/VaccinationType'
import { ReadOnlyItem } from '../../../common/ReadOnlyItem'
import { EditAnimalHealthFrequenciesModal } from './EditAnimalHealthFrequenciesModal'

interface Props {
  animal: AnimalDto
  canEditAnimal: boolean
}

type FormParams = {
  vaccinationStatus?: CreateAnimalBodyDtoVaccinationStatus
  vaccines?: AnimalVaccinationDtoVaccines[]
}

export const AnimalHealthVaccines: React.FC<Props> = ({ animal, canEditAnimal }) => {
  const editAnimalMutation = useEditAnimalMutation({})

  const getDefaultValues = (animal: AnimalDto): FormParams => ({
    vaccinationStatus: animal.vaccination.vaccinationStatus || undefined,
    vaccines: animal.vaccination.vaccines || [],
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useFormExtended(animal, getDefaultValues)

  useEffect(() => {
    const onSubmit = async (data: FormParams) => {
      const update = omitEmptyString({
        ...animal,
        vaccination: {
          ...animal.vaccination,
          vaccinationStatus: data.vaccinationStatus || undefined,
          vaccines: data.vaccines || undefined,
        },
      })

      await editAnimalMutation.mutate(update)
    }
    if (isDirty) {
      handleSubmit(onSubmit)()
    }
  }, [isDirty])
  return (
    <form>
      <Grid
        container
        columnSpacing={{ md: 1.5, xs: 1 }}
        rowSpacing={{ md: 1.5, xs: 1 }}
        sx={{ display: 'flex', justifyContent: 'center', margin: 0, width: '100%' }}
      >
        <Grid item xs={12} md={3}>
          <ColoredChipSelectField
            sx={{ minWidth: { md: 240 } }}
            control={control}
            error={errors.vaccinationStatus}
            fieldName="vaccinationStatus"
            size="small"
            label="Statut primovaccination"
            options={Object.values(CreateAnimalBodyDtoVaccinationStatus).map(
              (status: CreateAnimalBodyDtoVaccinationStatus) => ({
                label: getReadableAnimalVaccineStatus(status),
                value: status,
                color: getColorForAnimalVaccineStatus(status),
              })
            )}
            requiredRule="Le statut primovaccination est requis"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          {(animal.species === AnimalDtoSpecies.Cat || animal.species === AnimalDtoSpecies.Dog) && (
            <ControlledMultipleAutoComplete
              freeSolo={false}
              control={control}
              error={undefined}
              fieldName="vaccines"
              label="Type (Choississez un ou plusieurs vaccins)"
              options={
                animal.species === AnimalDtoSpecies.Cat
                  ? Object.values(AnimalVaccinationDtoVaccines)
                      .filter((vaccine) => isVaccineForCat(vaccine))
                      .map((vaccine: AnimalVaccinationDtoVaccines) => ({
                        label: getReadableVaccines(vaccine),
                        value: vaccine,
                      }))
                  : animal.species === AnimalDtoSpecies.Dog
                  ? Object.values(AnimalVaccinationDtoVaccines)
                      .filter((vaccine) => isVaccineForDog(vaccine))
                      .map((vaccine: AnimalVaccinationDtoVaccines) => ({
                        label: getReadableVaccines(vaccine),
                        value: vaccine,
                      }))
                  : []
              }
              requiredRule=""
              size="small"
            />
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: { xs: 1, md: 1.25 },
              borderRadius: 3,
              border: 'none',
              backgroundColor: 'transparent',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                minHeight: 32,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                Fréquence des rappels:
              </Typography>
              {canEditAnimal && (
                <EditAnimalHealthFrequenciesModal
                  animal={animal}
                  sx={{
                    color: '#F3AC00',
                    p: 0,
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    backgroundColor: 'rgba(243, 172, 0, 0.08)',
                  }}
                />
              )}
            </Box>
            <Grid container spacing={{ xs: 1, md: 1.5 }} alignItems="center">
              <Grid item xs={4}>
                <ReadOnlyItem
                  label="Post-primo"
                  value={
                    animal.vaccination.annualReminderFrequencyInYear === -1
                      ? 'Non'
                      : `${animal.vaccination.annualReminderFrequencyInYear || 1} an(s)`
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <ReadOnlyItem
                  label="Vermifuge"
                  value={
                    animal.health.dewormingFrequencyInMonths === -1
                      ? 'Non'
                      : animal.health.dewormingFrequencyInMonths === 0.5
                      ? '15 jours'
                      : `${animal.health.dewormingFrequencyInMonths || 1} mois`
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <ReadOnlyItem
                  label="Antipuce"
                  value={
                    animal.health.fleaControlFrequencyInMonths === -1
                      ? 'Non'
                      : animal.health.fleaControlFrequencyInMonths === 0.5
                      ? '15 jours'
                      : `${animal.health.fleaControlFrequencyInMonths || 1} mois`
                  }
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </form>
  )
}
